import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import { Building2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import PropertyCard from '../components/ui/PropertyCard';
import PropertyFiltersBar from '../components/filters/PropertyFilters';
import PropertyAddressFocus from '../components/properties/PropertyAddressFocus';
import DueDiligencePanel from '../components/properties/DueDiligencePanel';
import ScoringSliders, { computeWeightedScore } from '../components/filters/ScoringSliders';
import SearchProfileSelector from '../components/filters/SearchProfileSelector';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import DisabledMemberFeature from '../components/ui/DisabledMemberFeature';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/api';
import { hasBasicAccess, dueDiligenceHint } from '../utils/access';

const DEFAULT_FILTERS = { sort_by: 'investment_score', sort_dir: 'desc', page: 1, page_size: 18 };
const SAVE_DEBOUNCE_MS = 800;
const LS_KEY = 'assetlens_scoring_weights';

const NUMERIC_FILTER_KEYS = new Set([
  'page', 'page_size', 'radius_miles', 'min_score', 'min_yield', 'min_beds', 'max_beds',
]);

function paramsToFilters(searchParams) {
  const out = { ...DEFAULT_FILTERS };
  for (const [k, v] of searchParams.entries()) {
    if (v === '') continue;
    if (NUMERIC_FILTER_KEYS.has(k)) {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = n;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function filtersToParams(filters) {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v == null || v === '') return;
    if (k === 'page' && v === 1) return;
    if (k === 'page_size' && v === DEFAULT_FILTERS.page_size) return;
    if (k === 'sort_by' && v === DEFAULT_FILTERS.sort_by) return;
    if (k === 'sort_dir' && v === DEFAULT_FILTERS.sort_dir) return;
    p.set(k, String(v));
  });
  return p;
}

function loadLocalWeights() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function Properties() {
  const { user } = useAuth();
  const canUseFilters = hasBasicAccess(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const effectiveFilters = useMemo(() => {
    if (canUseFilters) return filters;
    return { ...DEFAULT_FILTERS, page: filters.page || 1 };
  }, [canUseFilters, filters]);
  const setFilters = useCallback((next) => {
    setSearchParams((prev) => {
      const current = paramsToFilters(prev);
      const resolved = typeof next === 'function' ? next(current) : next;
      return filtersToParams(resolved);
    });
  }, [setSearchParams]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weights, setWeights] = useState(null);
  const [savedWeights, setSavedWeights] = useState(null);
  const [usePersonalScore, setUsePersonalScore] = useState(false);
  const [dueDiligenceRun, setDueDiligenceRun] = useState(false);
  const saveTimerRef = useRef(null);
  const canDueDiligence = hasBasicAccess(user);

  useEffect(() => {
    setDueDiligenceRun(false);
  }, [filters.address_query]);

  // Load saved weights on mount
  useEffect(() => {
    const token = localStorage.getItem('assetlens_token');
    if (token) {
      fetch('/api/account/profile', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(profile => {
          if (profile?.scoring_preferences) {
            try {
              const parsed = JSON.parse(profile.scoring_preferences);
              setSavedWeights(parsed);
              setWeights(parsed);
              setUsePersonalScore(true);
            } catch {}
          }
        })
        .catch(() => {});
    } else {
      const local = loadLocalWeights();
      if (local) {
        setSavedWeights(local);
        setWeights(local);
        setUsePersonalScore(true);
      }
    }
  }, []);

  const fetchProperties = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(effectiveFilters).forEach(([k, v]) => { if (v != null) params.set(k, v); });
    authFetch(`/api/properties?${params}`)
      .then((r) => {
        if (!r.ok) {
          throw new Error(
            r.status === 401
              ? 'Session expired — please sign in again'
              : `Failed to load properties (${r.status})`,
          );
        }
        return r.json();
      })
      .then((d) => {
        if (!Array.isArray(d?.items) || typeof d.total !== 'number') {
          throw new Error('Unexpected response from properties API');
        }
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [effectiveFilters]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  // Re-sort properties client-side when weights change
  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    if (!usePersonalScore || !weights) return data.items;

    return [...data.items]
      .map(prop => ({
        ...prop,
        personalScore: computeWeightedScore(prop, weights),
      }))
      .sort((a, b) => (b.personalScore ?? -1) - (a.personalScore ?? -1));
  }, [data?.items, weights, usePersonalScore]);

  // Compute match stats for live counter
  const matchStats = useMemo(() => {
    if (!data?.items || !weights || !usePersonalScore) return null;
    const scores = data.items
      .map(p => computeWeightedScore(p, weights))
      .filter(s => s !== null);
    if (scores.length === 0) return null;
    const above50 = scores.filter(s => s >= 50).length;
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    return { above50, total: scores.length, avg };
  }, [data?.items, weights, usePersonalScore]);

  const handleWeightsChange = useCallback((w) => {
    setWeights(w);
    setUsePersonalScore(true);

    // Debounced save
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      // Always save to localStorage
      localStorage.setItem(LS_KEY, JSON.stringify(w));

      // Save to profile if logged in
      const token = localStorage.getItem('assetlens_token');
      if (token) {
        fetch('/api/account/profile', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ scoring_preferences: JSON.stringify(w) }),
        }).catch(() => {});
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Apply an activated search profile: push its filter values into URL params and weights into state.
  const applyProfile = (profile) => {
    if (!profile) return;
    const f = { ...DEFAULT_FILTERS };
    ['postcode','center_postcode','property_type','price_band'].forEach(k => {
      if (profile[k] != null) f[k] = profile[k];
    });
    ['radius_miles','min_beds','max_beds','min_price','max_price','min_score','min_yield'].forEach(k => {
      if (profile[k] != null) f[k] = profile[k];
    });
    setFilters(f);
    if (profile.scoring_weights) {
      setSavedWeights(profile.scoring_weights);
      setWeights(profile.scoring_weights);
      setUsePersonalScore(true);
    } else {
      setUsePersonalScore(false);
    }
  };

  const clearProfile = () => {
    // 'All Stock' — keep current filters, just deactivate the profile
    // (fresh defaults would surprise users mid-flow).
  };

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white">Properties</h1>
        <p className="text-slate-400 text-sm mt-1">
          {data?.total != null ? (
            user && canUseFilters && effectiveFilters.address_query
              ? `${data.total.toLocaleString()} ${data.total === 1 ? 'property' : 'properties'} matching address`
              : `${data.total.toLocaleString()} properties found`
          ) : 'Loading...'}
          {usePersonalScore && weights && (
            <span className="ml-2 text-blue-400">
              — sorted by your preferences
            </span>
          )}
        </p>
      </div>

      {canUseFilters ? (
        <SearchProfileSelector
          currentFilters={filters}
          currentWeights={weights}
          onActivate={applyProfile}
          onCleared={clearProfile}
        />
      ) : (
        <DisabledMemberFeature
          title="Saved searches"
          hint="Upgrade to Basic or Pro to save filter presets and switch between your search profiles."
        />
      )}

      {canUseFilters ? (
        <PropertyFiltersBar
          filters={filters}
          onChange={(f) => { setFilters(f); setUsePersonalScore(false); }}
          onReset={() => { setFilters(DEFAULT_FILTERS); setUsePersonalScore(false); }}
          disabled={false}
          onDueDiligence={filters.address_query ? () => setDueDiligenceRun(true) : undefined}
          dueDiligenceEnabled={canDueDiligence}
        />
      ) : (
        <DisabledMemberFeature
          title="Property filters"
          hint="Upgrade to Basic or Pro to search by postcode or address, sort, and filter the property list."
        />
      )}

      {user && filters.address_query && !canDueDiligence && (
        <p className="text-xs text-slate-500">
          {dueDiligenceHint(user)}{' '}
          <Link to="/choose-plan" className="text-emerald-400 hover:underline">View plans</Link>
        </p>
      )}

      {user && filters.auction_house && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm">
          <span>
            Showing lots from <strong className="capitalize">{filters.auction_house.replace(/_/g, ' ')}</strong> only.
          </span>
          <button
            type="button"
            onClick={() => setFilters({ ...filters, auction_house: undefined, page: 1 })}
            className="text-xs px-2 py-1 rounded-md bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {canUseFilters ? (
        <ScoringSliders
          onWeightsChange={handleWeightsChange}
          initialWeights={savedWeights}
          matchStats={matchStats}
        />
      ) : (
        <DisabledMemberFeature
          title="Scoring preferences"
          hint="Upgrade to Basic or Pro to adjust investment scoring sliders and sort by your personal score."
        />
      )}

      {user && data?.search_mode === 'address' && sortedItems.length > 0 && (
        <PropertyAddressFocus
          propertyId={sortedItems[0].id}
          matchCount={data.total}
          viewerAccess={data.viewer_access}
        />
      )}

      {user && effectiveFilters.address_query && !loading && !error && (data?.total === 0 || dueDiligenceRun) && (
        <DueDiligencePanel
          address={effectiveFilters.address_query}
          canRun={canDueDiligence}
          autoRun={dueDiligenceRun}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner size={40} className="text-emerald-500" />
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-3 p-5 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <AlertTriangle size={20} className="text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          <AnimatePresence mode="wait">
            <motion.div
              key={JSON.stringify(filters) + String(usePersonalScore)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            >
              {sortedItems.length === 0 ? (
                filters.address_query ? null : (
                  <div className="col-span-3 text-center py-20">
                    <Building2 size={48} className="text-slate-700 mx-auto mb-3" />
                    <p className="text-slate-500">No properties match your filters</p>
                  </div>
                )
              ) : (
                sortedItems.map((prop, i) => (
                  <motion.div
                    key={prop.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                  >
                    <PropertyCard
                      property={prop}
                      personalScore={prop.personalScore}
                      viewerAccess={data?.viewer_access}
                    />
                  </motion.div>
                ))
              )}
            </motion.div>
          </AnimatePresence>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <button
                disabled={filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-slate-400 text-sm">
                Page {filters.page} of {data.pages}
              </span>
              <button
                disabled={filters.page >= data.pages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
